import dayjs from "dayjs";
import { BdcBillingCheck, BdcBillingRow } from "../../../types/bdc.type";
import { thaiDate } from "../format";

/** The default range of a new ใบวางบิล: the whole previous month of `today` (`YYYY-MM-DD`). */
export const previousMonthRange = (today: string): { from: string; to: string } => {
  const first = dayjs(today).startOf("month").subtract(1, "month");
  return { from: first.format("YYYY-MM-DD"), to: first.endOf("month").format("YYYY-MM-DD") };
};

/** Why a range cannot be checked: reversed, or across two months (one ใบวางบิล = one month). */
export const billingRangeProblem = (from: string, to: string): string | null => {
  if (!from || !to) return "เลือกช่วงวันที่ INV";
  if (from > to) return "วันที่เริ่มต้องไม่หลังวันที่สิ้นสุด";
  if (from.slice(0, 7) !== to.slice(0, 7)) return "ช่วงวันที่ต้องอยู่ในเดือนเดียวกัน (ใบวางบิลเดือนละใบ)";
  return null;
};

/**
 * วันครบกำหนดตั้งต้น — the 5th of the month after the document date, moved off a weekend to the
 * Monday (no holiday table yet). The server suggests the same; this is the fallback.
 */
export const defaultDueDate = (docDate: string): string => {
  let due = dayjs(docDate).startOf("month").add(1, "month").date(5);
  if (due.day() === 6) due = due.add(2, "day");
  else if (due.day() === 0) due = due.add(1, "day");
  return due.format("YYYY-MM-DD");
};

export const dueDateProblem = (docDate: string, dueDate: string): string | null => {
  if (!docDate || !dueDate) return "เลือกวันที่เอกสารและวันครบกำหนด";
  if (dueDate < docDate) return "วันครบกำหนดต้องไม่ก่อนวันที่เอกสาร";
  return null;
};

export const countChecks = (checks: BdcBillingCheck[]) => ({
  errors: checks.filter((c) => c.level === "error").length,
  warnings: checks.filter((c) => c.level === "warning").length,
});

/** Errors first, then warnings, each in the server's order. */
export const sortChecks = (checks: BdcBillingCheck[]): BdcBillingCheck[] => [
  ...checks.filter((c) => c.level === "error"),
  ...checks.filter((c) => c.level !== "error"),
];

export type CheckDetailView =
  | { kind: "table"; columns: string[]; rows: Record<string, unknown>[] }
  | { kind: "list"; items: string[] }
  | { kind: "pairs"; pairs: [string, string][] }
  | null;

const cellText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(cellText).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * How to show a check's `detail` (free-form on the server): a list of records → a table (the
 * INV-per-day-per-DC table), a list of values → a comma list (missing numbers, DCs), an object →
 * key: value lines. An object holding one list of records (`{ rows: [...] }`) shows that list.
 */
export const checkDetailView = (detail: unknown): CheckDetailView => {
  if (detail === null || detail === undefined || detail === "") return null;
  if (Array.isArray(detail)) {
    if (detail.length === 0) return null;
    if (detail.every((d) => d && typeof d === "object" && !Array.isArray(d))) {
      const rows = detail as Record<string, unknown>[];
      const columns = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
      return { kind: "table", columns, rows };
    }
    return { kind: "list", items: detail.map(cellText) };
  }
  if (typeof detail === "object") {
    const entries = Object.entries(detail as Record<string, unknown>);
    const lists = entries.filter(([, v]) => Array.isArray(v) && v.length > 0);
    if (entries.length === 1 && lists.length === 1) return checkDetailView(lists[0][1]);
    return { kind: "pairs", pairs: entries.map(([k, v]) => [k, cellText(v)]) };
  }
  return { kind: "list", items: [cellText(detail)] };
};

/** The rows' totals as the file sums them (CN rows are negative). */
export const sumRows = (rows: Pick<BdcBillingRow, "subtotal" | "vat_amount" | "grand_total">[]) =>
  rows.reduce(
    (acc, r) => ({
      subtotal: acc.subtotal + r.subtotal,
      vat_amount: acc.vat_amount + r.vat_amount,
      grand_total: acc.grand_total + r.grand_total,
    }),
    { subtotal: 0, vat_amount: 0, grand_total: 0 },
  );

/** `2026-09-01` → `ก.ย. 2569` — the month a ใบวางบิล covers. */
export const thaiMonth = (periodMonth: string): string => thaiDate(`${periodMonth.slice(0, 7)}-01`).replace(/^1 /, "");
